import React from 'react';
import { CollapseFAQ, H3, LayoutSingleColumn, Page } from '../../components';
import SectionFooter from '../PageBuilder/SectionBuilder/SectionFooter';
import TopbarContainer from '../TopbarContainer/TopbarContainer';

import { useIntl } from '../../util/reactIntl';

import DogImage from './images/Pic.1.png';
import ParisImage from './images/Alecia housesitter in Paris.jpg';
import DogImage2 from './images/Chloe looking at the 3 Westies - HouseSit Match 2023-157.jpg';
import css from './BenefitsHouseSittersPage.module.css';

import { defaultFaq } from './faq';

const BenefitsHouseSittersPage = props => {

  const intl = useIntl();
  const schemaTitle = intl.formatMessage({ id: 'BenefitsHouseSittersPage.schemaTitle' });
  const schemaDescription = intl.formatMessage({ id: 'BenefitsHouseSittersPage.schemaDescription' });

  return (
    <Page
      title={schemaTitle}
      description={schemaDescription}
    >
      <LayoutSingleColumn
        topbar={<TopbarContainer />}
        footer={<SectionFooter />}
        mainColumnClassName={css.mainColumnClassName}
      >
        <div className={css.root}>
          <H3>
            Benefits of Becoming a House Sitter or Pet Sitter
          </H3>

          <p className={css.info}>
            House sitting offers exciting opportunities to travel the world, stay in unique locations, and experience life as a local while caring for homes and pets. By becoming a house sitter or pet sitter with HouseSit Match, you gain access to a global network of homeowners seeking trustworthy sitters for their homes and pets.
          </p>

          <p className={css.info}>
            Whether you're looking to explore new destinations or simply want to save money on accommodation, our house sitting platform provides the flexibility and resources you need to make it happen.
          </p>

          <div className={css.imageContainer}>
            <img className={css.image} src={DogImage} alt='travel is one of the benefits of house sitting for sitters' />
            <p className={css.info}>
              Travel is one of the benefits of house sitting for sitters. This is Jill in Australia.
            </p>
          </div>

          <p className={css.subtitle}>
            Benefits of House Sitting for Sitters
          </p>
          <p className={css.info}>
            As a house sitter, you can enjoy a cost-effective way to travel and experience new places, all while caring for homes and pets. This unique arrangement provides multiple benefits that go beyond what you see on the surface level.
          </p>
          <ul className={css.list}>
            <li>
              <strong>Free Accommodation: </strong>Exchange your time and care for free stays in homes around the world.
            </li>
            <li>
              <strong>Affordable Travel: </strong>Save on rent or hotel costs, allowing you to travel further and for longer.
            </li>
            <li>
              <strong>Cultural Immersion: </strong>Experience new places like a local by caring for someone's home and pets.
            </li>
            <li>
              <strong>Diverse Locations: </strong>Choose from a range of sit opportunities in cities, countryside, or exotic destinations.
            </li>
            <li>
              <strong>Flexible Timing: </strong>Tailor your sit around your schedule, whether for short stays or longer commitments.
            </li>
          </ul>
          <p className={css.info}>
            Becoming a house sitter not only saves you money but also offers the chance to enrich your travel experiences with local knowledge and unique connections.
          </p>

          <p className={css.subtitle}>
            Benefits of Pet Sitting for Sitters
          </p>
          <p className={css.info}>
            Pet sitting is the perfect way to combine your love of animals with your passion for travel. At HouseSit Match, pet sitters enjoy a number of benefits when they choose to care for pets while staying in comfortable, well-maintained homes.
          </p>
          <ul className={css.list}>
            <li>
              <strong>Pet Care in Familiar Environments:</strong> Offer pets the comfort of their own home, maintaining their routine and reducing stress.
            </li>
            <li>
              <strong>Meaningful Connections:</strong> Build lasting bonds with pets and homeowners alike.
            </li>
            <li>
              <strong>Save on Accommodation:</strong> Enjoy free accommodation in exchange for caring for pets.
            </li>
            <li>
              <strong>Personalised Care:</strong> Adapt your services to fit the pet's specific needs, whether that’s playtime, feeding schedules, or medication administration.
            </li>
            <li>
              <strong>Global Opportunities:</strong> Pet sitting can take you around the world, from the UK to far-flung destinations.
            </li>
          </ul>
          <p className={css.info}>
            For those passionate about animals, pet sitting with HouseSit Match provides both a rewarding experience and the chance to travel affordably.
          </p>
          <div className={css.imageContainer}>
            <img className={css.image} src={ParisImage} alt="houses sitter in Paris" />
            <p className={css.info}>
              Many housesitters choose HouseSit Match because of our European housesits. This is Alecia in Paris.
            </p>
          </div>

          <p className={css.subtitle}>
            Why Choose HouseSit Match for Your House Sitting Journey?
          </p>
          <p className={css.info}>
            HouseSit Match is committed to making your house and pet sitting experiences smooth, rewarding, and secure. Whether you're an experienced sitter or new to the world of house sitting, our platform offers the tools, resources, and support you need to succeed.
          </p>
          <ul className={css.list}>
            <li>
              <strong>Global Opportunities:</strong> With access to house and pet sitting opportunities worldwide, you can choose your ideal locations, whether you're looking for a local adventure or an international experience.
            </li>
            <li>
              <strong>Trust and Safety:</strong> Our thorough ID verification and secure messaging system ensure a safe, trustworthy environment for all members. We take steps to verify both sitters and homeowners, helping you feel confident in your arrangements.
            </li>
            <li>
              <strong>Free Video Calls:</strong> Get introduced to your house sitters online, get to know them and keep in contact if you need to.
            </li>
            <li>
              <strong>Support Every Step of the Way:</strong> From building your profile to securing your first sit, we offer guidance and resources to help you succeed. Our team is always available to assist via live chat, email, or phone.
            </li>
            <li>
              <strong>Transparent Reviews:</strong> Read real, verified reviews from homeowners to help you make informed decisions before committing to a sit. Your own reviews also help build your credibility and attract more opportunities.
            </li>
            <li>
              <strong>Customer Support:</strong> We provide ongoing assistance, ensuring that any issues or concerns are addressed quickly and effectively. Whether you're new to house sitting or a seasoned sitter, you can count on us to be there for you.
            </li>
            <li>
              <strong>24/7 Access to Vet Professionals:</strong> (as a Choice or Premium Member) Call a professional vet nurse with minimum 5 years experience for a consultation. They can help you decide how to support your pet in case of unexpected poor health.
            </li>
          </ul>
          <p className={css.info}>
            HouseSit Match is your trusted partner in your house and pet sitting journey, offering everything you need to make your experience seamless, secure, and enjoyable.
          </p>
          <div className={css.imageContainer}>
            <img className={css.image} src={DogImage2} alt=" pet sitter minding three dogs" />
            <p className={css.info}>
              HouseSit Match can support you with the 24/7 Vet Helpline when you are caring for pets in a housesit
            </p>
          </div>

          <p className={css.subtitle}>
            How HouseSit Match Supports House Sitters and Pet Sitters
          </p>

          <p className={css.info}>
            At HouseSit Match, we’re committed to helping house sitters and pet sitters make the most of their experiences. From creating a standout profile to finding the perfect house sit, we offer extensive support throughout the entire process.
          </p>
          <ul className={css.list}>
            <li>
              <strong>Profile Creation Assistance:</strong> We guide you through building a profile that highlights your skills and experiences to attract homeowners.
            </li>
            <li>
              <strong>Guidelines and Templates:</strong> Access helpful templates and checklists to set clear expectations with homeowners and ensure a smooth sit.
            </li>
            <li>
              <strong>Step-by-Step Support:</strong> From your first sit to your hundredth, we provide continuous guidance to help you succeed in your journey.
            </li>
            <li>
              <strong>Security and Trust:</strong> Our platform includes secure messaging, ID verification, and a dispute resolution system to ensure a safe experience for both sitters and homeowners.
            </li>
            <li>
              <strong>Flexible Membership Options:</strong> Choose between Standard and Premium memberships, both offering tailored features that suit your needs as a house sitter.
            </li>
          </ul>
          <p className={css.info}>
            HouseSit Match is more than just a platform—it's a partner in your house and pet sitting journey, ensuring you feel supported at every step.
          </p>


          <p className={css.subtitle}>
            Flexible Membership Options
          </p>
          <p className={css.info}>
            HouseSit Match offers flexible membership options to suit your needs as a house sitter. Choose the plan that works best for your travel goals, Value Match, Choice Match or Premium Match, and access additional features to enhance your experience.
          </p>

          <ul >
            <li>
              <div className='mt20'>
                <strong>Value Match Membership (£89/year)</strong>
              </div>
              <ul className={css.list}>
                <li>Unlimited housesitting</li>
                <li>Free video calls with homeowners</li>
                <li>Free Live chat support</li>
                <li>Verified reviews</li>
                <li>Up to 7 profile images</li>
                <li>Free ID verification</li>
                <li>Discounts available for upgrades</li>
              </ul>
            </li>
            <li>
              <div className='mt20'>
                <strong>Choice Match Membership (£129/year)</strong>
              </div>
              <ul className={css.list}>
                <li>Unlimited housesitting</li>
                <li>Free video calls with homeowners</li>
                <li>Free live chat support</li>
                <li>Verified reviews</li>
                <li>Up to 15 profile photos</li>
                <li>Free access to 24/7 vet professionals support</li>
                <li>Discounts available for upgrades</li>
              </ul>
            </li>
            <li>
              <div className='mt20'>
                <strong>Premium Match Membership (£169/year)</strong>
              </div>
              <ul className={css.list}>
                <li>Unlimited housesitting</li>
                <li>Free video calls with homeowners</li>
                <li>Free client account support with a team member</li>
                <li>Verified reviews</li>
                <li>Up to 20 images and video introductions</li>
                <li>Free access to 24/7 vet professional support</li>
                <li>Access to personalised alerts</li>
                <li>Discounts available for upgrades to combined accounts</li>
                <li>Free facility to boost your profile</li>
                <li>Free account management support</li>
              </ul>
            </li>
          </ul>

          <p className={css.subtitle}>
            How to Join as a House Sitter
          </p>
          <p className={css.info}>
            Getting started with HouseSit Match is easy! Follow these 3 simple steps to create your account and start connecting with homeowners looking for reliable sitters:
          </p>
          <ul className={css.listNumbers}>
            <li>
              <strong>Register Your Account</strong> – Sign up as a house sitter to create your profile.
            </li>
            <li>
              <strong>Complete Your ID Verification</strong> – For your security, all members complete an ID check. We’re here to guide you through the process.
            </li>
            <li>
              <strong>Build Your Profile</strong> – Add details about your experience, pet care skills, and your ideal sits. Premium members can upload more images and videos to enhance their profile.
            </li>
          </ul>
          <p className={css.info}>
            Once your profile is ready, start browsing house sitting opportunities, apply for sits, and connect with homeowners in need of your services.
          </p>

          <p className={css.subtitle}>
            FAQs from House Sitters and Pet Sitters
          </p>

          <CollapseFAQ
            list={defaultFaq}
          />

          <p className={css.subtitle}>
            Start Your Journey as a House Sitter
          </p>
          <p className={css.info}>
            Ready to start your adventure? Join HouseSit Match today to begin applying for house and pet sitting opportunities. Whether you’re looking to travel on a budget or seeking a new adventure, HouseSit Match has the tools and support you need to make it happen.
          </p>
          <p className={css.info}>
            Choose the membership plan that suits your goals, and start enjoying free accommodation and the chance to experience the world like never before.
          </p>

        </div>
      </LayoutSingleColumn>
    </Page>
  );
};

export default BenefitsHouseSittersPage;
